import {
  DateFormat,
  NullableDateInput,
  stringifyDate,
} from '@superdispatch/dates';
import {
  CanadaProvince,
  CanadaProvinceOptionsSet,
} from 'shared/form/CanadaProvince';
import { countryFieldOptions } from 'shared/form/CountryFieldOptions';
import { USAState, USAStateOptions } from 'shared/form/USAState';
import { z } from 'zod';

const zodString = z
  .string()
  .nullish()
  .transform((value) => value ?? '');

const zodBoolean = z.preprocess(
  (value) =>
    typeof value === 'string' || typeof value === 'number'
      ? /^(true|1)$/i.test(String(value))
      : value,
  z.boolean().nullable().default(false).catch(false),
);

const zodNumber = z.preprocess(
  // `Number('')` is `0`, which would turn a cleared numeric input into a value.
  (value) => (value == null || value === '' ? null : Number(value)),
  z.number().nullable().default(null).catch(null),
);

const zodRequiredNumber = z.preprocess(
  (value) => (value == null ? value : Number(value)),
  z.number().default(0).catch(0),
);

// `infinity` marks a tier with no upper bound, and `z.number()` rejects it.
const zodEndingSeat = z.preprocess(
  (value) =>
    value === 'infinity' ? Infinity : value == null ? value : Number(value),
  z
    .custom<number>(
      (value) => typeof value === 'number' && !Number.isNaN(value),
    )
    .catch(0),
);

function zodDateString(format: DateFormat) {
  return z.preprocess(
    (value) => stringifyDate(value as NullableDateInput, { format }),
    z.string().nullable().default(null),
  );
}

export type TaxPriceDTO = z.infer<typeof taxPriceSchema>;
const taxPriceSchema = z.object({
  sub_total: zodNumber,
  total: zodString,
  taxes: z
    .array(
      z.object({
        name: zodString,
        description: zodString,
        amount: zodNumber,
      }),
    )
    .prefault([]),
  plan: z
    .object({
      amount: zodNumber,
      measurement_type: z
        .enum(['driver_seat', 'user_seat'])
        .nullable()
        .catch(null),
      name: z.enum(['lite', 'pro']).nullable().catch(null),
      quantity: zodNumber,
      unit_price: zodNumber,
    })
    .nullable(),
  past_due_invoices: z
    .array(
      z.object({
        guid: zodString,
        status: zodString,
        due_at: zodDateString('DateISO'),
        amount_due: zodNumber,
      }),
    )
    .prefault([])
    .nullable(),
});

export type PastDueInvoicesDTO = z.infer<typeof pastDueInvoicesSchema>;
export const pastDueInvoicesSchema = z.object({
  sub_total: zodNumber,
  total: zodNumber,
  taxes: taxPriceSchema.shape.taxes,
  past_due_invoices: taxPriceSchema.shape.past_due_invoices,
});

export type SubscriptionSettingsDTO = z.infer<
  typeof subscriptionSettingsSchema
>;
const subscriptionSettingsSchema = z.object({
  client_token: zodString,
  plan: z
    .object({
      type: zodString,
      drivers_count: zodNumber,
      price: zodNumber,
      discounted_price: zodNumber,
      discount_amount: zodNumber,
      seats_count: zodNumber,
      seats_pricing_tiers: z
        .array(
          z.object({
            starting_seat: zodRequiredNumber,
            ending_seat: zodEndingSeat,
            price: zodNumber,
          }),
        )
        .prefault([]),
    })
    .nullable(),

  card_information: z
    .object({
      card_number: zodString,
      cardholder_name: zodString,
      // this field contains `MM/YYYY` format date string
      expiry: zodString,
    })
    .nullable(),
  status: z
    .enum(['Active', 'Canceled', 'Past Due', 'Pending', 'In Trial'])
    .nullable()
    .catch(null),
  billing_period_end_date: zodDateString('DateISO'),
});

export type BillingAddressDTO = z.infer<typeof billingAddressSchema>;
const billingAddressSchema = z.object({
  billing_zip: zodString,
  billing_state: zodString,
  billing_country: zodString,
  billing_address: zodString,
  billing_city: zodString,
});

const MIN_SEATS_COUNT = 1;

export const subscriptionContactSchema = z.object({
  contact_first_name: z
    .string()
    .nullish()
    .transform((value) => value?.trim() ?? '')
    .pipe(
      z
        .string()
        .min(1, 'This field is required')
        .max(150, 'Must be 150 characters or less'),
    ),
  contact_last_name: z
    .string()
    .nullish()
    .transform((value) => value?.trim() ?? '')
    .pipe(
      z
        .string()
        .min(1, 'This field is required')
        .max(150, 'Must be 150 characters or less'),
    ),
  contact_email: z
    .string()
    .nullish()
    .transform((value) => value?.trim() ?? '')
    .pipe(
      z
        .string()
        .min(1, 'This field is required')
        .pipe(z.email('Enter a valid email')),
    ),
});

export interface UpdateSubscriptionContactDTO {
  first_name: string;
  last_name: string;
  email: string;
}

export const subscriptionSchema = z
  .object({
    country: z
      .enum([...countryFieldOptions.keys()])
      .nullable()
      .catch(null),
    state: zodString,
    zip: zodString,
    address: zodString,
    city: zodString,
    can_use_company_billing_address: zodBoolean.optional(),
    seats_count: zodNumber.optional(),
  })
  .superRefine((values, ctx) => {
    const { country, seats_count } = values;

    if (!country) {
      ctx.addIssue({
        code: 'custom',
        path: ['country'],
        message: 'This field is required',
      });
    }

    if (seats_count != null && seats_count < MIN_SEATS_COUNT) {
      ctx.addIssue({
        code: 'custom',
        path: ['seats_count'],
        message: `Value must be greater than or equal to ${MIN_SEATS_COUNT}`,
      });
    }

    if (values.can_use_company_billing_address) {
      return;
    }

    if (country === 'US' && !USAStateOptions.has(values.state as USAState)) {
      ctx.addIssue({
        code: 'custom',
        path: ['state'],
        message: 'Select valid state',
      });
    }

    if (
      country === 'CA' &&
      !CanadaProvinceOptionsSet.has(values.state as CanadaProvince)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['state'],
        message: 'Select valid province',
      });
    }

    if (country !== 'US' && country !== 'CA') {
      return;
    }

    for (const field of ['zip', 'address', 'city'] as const) {
      if (!values[field]) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: 'This field is required',
        });
      }
    }
  });

export type SubscriptionDetailsDTO = z.infer<typeof subscriptionDetailsSchema>;
const subscriptionDetailsSchema = z.object({
  client_token: z.string().nullable().catch(null),

  customer: z
    .object({
      billing_address: z
        .object({
          address: zodString,
          city: zodString,
          country: zodString,
          state: zodString,
          zip: zodString,
        })
        .nullable(),
      payment_method: z
        .object({
          type: z.enum(['direct_debit', 'card']).catch('direct_debit'),
          bank_account: z
            .object({
              bank_name: zodString,
              name_on_account: zodString,
              last4: zodString,
            })
            .nullable(),
          card: z
            .object({
              masked_number: z.string().nullable().catch(null),
              expiry_year: zodNumber,
              expiry_month: zodNumber,
            })
            .nullable(),
        })
        .nullable(),
    })
    .nullable(),
  subscription: z
    .object({
      current_term_end: zodDateString('DateISO'),
      status: z
        .enum([
          'active',
          'canceled',
          'non_renewing',
          'future',
          'in_trial',
          'paused',
        ])
        .catch('active'),
      plan: z
        .object({
          amount: zodNumber,
          measurement_type: z
            .enum(['driver_seat', 'user_seat'])
            .catch('driver_seat'),
          name: z.enum(['lite', 'pro']).catch('lite'),
          period: z.enum(['month', 'year']).catch('month'),
          pricing_model: z
            .enum(['flat_fee', 'volume', 'per_unit', 'tiered', 'stairstep'])
            .catch('flat_fee'),
          quantity: zodNumber,
          unit_price: zodNumber,
        })
        .nullable(),
      contact: z
        .object({
          first_name: zodString,
          last_name: zodString,
          email: zodString,
        })
        .nullable()
        .catch(null),
    })
    .nullable(),
  upgrade_option: z
    .object({
      plan: z.object({
        measurement_type: z
          .enum(['driver_seat', 'user_seat'])
          .catch('driver_seat'),
      }),
    })
    .nullable(),
});

export type SubscriptionBillableSeatsDTO = z.infer<
  typeof subscriptionBillableSeatsSchema
>;
const subscriptionBillableSeatsSchema = z.object({
  billable_seats: zodNumber,
  reserved_seats: zodNumber,
  measurement_type: z.enum(['driver_seat', 'user_seat']).catch('driver_seat'),
});

export type UpdateSubscriptionDTO = z.infer<typeof updateSubscriptionSchema>;
const updateSubscriptionSchema = z.object({
  id: zodString,
  id_at_vault: zodString,
  payment_method_nonce: zodString,
  seats_count: z
    .preprocess(
      (value) => (value == null || value === '' ? null : Number(value)),
      z
        .number()
        .min(
          MIN_SEATS_COUNT,
          `Value must be greater than or equal to ${MIN_SEATS_COUNT}`,
        )
        .nullable(),
    )
    .optional(),
});
